import { Component, effect, EventEmitter, Input, Output, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Greige } from '../../../models/item/greige';
import { EnumValues, ModalService } from '@card-software/library';
import { EnumService } from '../../../services/enum.service';
import { setOptionsForPrimeNg } from '../../../PrimeNG/utils';
import { LabDipRequestProxyService } from '../../../proxies/item/lab-dip-request-proxy.service';
import { DyeingColor } from '../../../models/item/lab-dip';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import _ from 'lodash';
import { GarmentDetailForm, WorkOrderFabricFormGroup } from '../../work-order/work-order-details/work-order-details.component';
import { searchColorByFabric } from '../../../models/lab-dip-request/search-color';
import { debounceTime, lastValueFrom } from 'rxjs';
import { FabricAliasManageModalComponent } from '../../_modals/fabric-alias-manage-modal/fabric-alias-manage-modal.component';
import { CustomerProductOrders, WorkOrderType } from '../../../models/order/work-order';
import { Business } from '../../../models/finance/business';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { Composition } from '../../../models/yarn/composition';
import { Certification, ItemMaster } from '../../../models/item/item-master/item-master';
import { MatchingItemMasterCommand, Yarn } from '../../../models/yarn/yarn-search';
import { MultiSelectChangeEvent } from 'primeng/multiselect';
import { YarnSearchProxyService } from '../../../proxies/item/yarn.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';

@Component({
  selector: 'app-fabric-details-panel',
  templateUrl: './fabric-details-panel.component.html',
  styleUrl: './fabric-details-panel.component.scss',
})
export class FabricDetailsPanelComponent implements OnInit, OnChanges {
  @Input() isShortFall = false;
  @Input() canEdit = false;
  @Input() canEditPackingType = false;
  @Input() orderType = 0;
  @Input() workOrderFabricForm: FormGroup<WorkOrderFabricFormGroup> = {} as FormGroup<WorkOrderFabricFormGroup>;
  @Input() customerColors: DyeingColor[] = [];
  @Input() customer: Business | null = null;
  @Input() orderDetailsFormSubmitted = false;
  @Input() uom = '';
  @Input() clientProductOrders: CustomerProductOrders[] = [];
  @Output() removeFabric = new EventEmitter<string>();
  @Output() savePackingType = new EventEmitter<{ fabricCode: string; packingType: number }>();
  @Input() allCompositions: Composition[] = [];
  @Input() certifications: Certification[] = [];

  isPackingTypeEditMode = false;
  private originalPackingType: number | null = null;
  public greige: Greige | null = null;
  // Forms and Controls
  totalCost = new FormControl({ value: 0, disabled: true });
  colorSearch = new FormControl();
  totalQty = new FormControl({ value: 0, disabled: true });
  // toggle data options
  dataView: FormControl<'general' | 'composition' | null> = new FormControl('general');
  dataOptions: { label: string; value: 'general' | 'composition' }[] = [
    { label: 'General', value: 'general' },
    { label: 'Composition & Certifications', value: 'composition' },
  ];
  yarnUniqueKeyAndItemMaster: Record<string, ItemMaster | null> = {};
  // Enums
  public garmetPartType: EnumValues = {};
  public garmentPartOptions: { label: string; value: number }[] = [];
  public packingTypeEnums: EnumValues = {};
  public packingTypeOptions: { label: string; value: number }[] = [];
  public WorkOrderType = WorkOrderType;
  fabricFormEnumsVals: EnumValues = {};
  yarnCertificationsMap: Record<string, Certification[]> = {};
  readonly emptyArray: Certification[] = [];
  setOptionsForPrimeNg = setOptionsForPrimeNg;
  // Customer Colors
  filteredColors: DyeingColor[] = [];
  clientProductOrderOptions: { label: string; value: string }[] = [];
  aliasOptions: { label: string; value: string }[] = [];

  getYarnByCode(code: string): Yarn | null {
    const greige: Greige | undefined = this.workOrderFabricForm.getRawValue().fabric?.greige;
    return greige?.yarnDetails.find((yd) => yd.yarn?.code === code)?.yarn || null;
  }

  get selectedCertifications(): Certification[] {
    const certificationReferences: string[] = _.uniq(
      (this.workOrderFabricForm.getRawValue().yarnCertificationDetails ?? []).flatMap((yarn) => yarn.certificationReferences ?? []),
    );

    return this.certifications.filter((cert) => certificationReferences.includes(cert.id) && !_.isNil(cert));
  }

  /** Greige and B-grade orders are priced and quantified per fabric, not per color. */
  get isGreigeOrder(): boolean {
    return (
      this.orderType === WorkOrderType.GreigeStockForDyeing ||
      this.orderType === WorkOrderType.GreigeOnly ||
      this.orderType === WorkOrderType.BGrade
    );
  }

  get hasGreigeData(): boolean {
    const fabric = this.workOrderFabricForm.getRawValue().fabric;
    return !_.isNil(fabric?.greige);
  }

  get fabricComposition() {
    return this.workOrderFabricForm.getRawValue().fabric?.greige?.fabricComposition || [];
  }

  get fabricFormValue() {
    const fabric = this.workOrderFabricForm.getRawValue().fabric;
    return fabric?.fabricForm !== undefined && fabric?.fabricForm !== null ? this.fabricFormEnumsVals[fabric.fabricForm] : '';
  }

  get gramsPerSquareMeter() {
    return this.workOrderFabricForm.getRawValue().fabric?.gramsPerSquareMeter || '';
  }

  get endToEndWidth() {
    return this.workOrderFabricForm.getRawValue().fabric?.endToEndWidth || '';
  }

  getItemMasterByUniqueKey(yarnCode: string, fabricCode: string): ItemMaster | null {
    const yarnUniqueKey = this.buildUniqueKey(yarnCode, fabricCode);
    return this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] || null;
  }

  constructor(
    private fb: FormBuilder,
    private enumService: EnumService,
    private labDipRequestProxyService: LabDipRequestProxyService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private yarnProxyService: YarnSearchProxyService,
    private localeHelperService: LocaleHelperService,
  ) {
    effect(() => {
      this.garmetPartType = this.enumService.getEnum('GarmetPartType', 'orderEnums').values;
      this.garmentPartOptions = setOptionsForPrimeNg(this.garmetPartType);
      this.packingTypeEnums = this.enumService.getEnum('PackingType', 'orderEnums').values;
      this.packingTypeOptions = setOptionsForPrimeNg(this.packingTypeEnums);
      this.fabricFormEnumsVals = this.enumService.getEnum('FabricForm', 'itemEnums').values;
    });
  }

  ngOnInit(): void {
    this.workOrderFabricForm.valueChanges.subscribe(() => {
      // A greige or B-grade order has no garment details to add up
      if (this.isGreigeOrder) {
        return;
      }
      this.workOrderFabricForm.controls.quantityInUOM.setValue(this.getTotalQuantity(), { emitEvent: false });
    });

    const savedAliasReference = this.workOrderFabricForm.get('aliasReference')?.value;
    if (!savedAliasReference) {
      this.updateAliasOptions();
      if (this.aliasOptions.length > 0) {
        this.workOrderFabricForm.get('aliasReference')?.setValue(this.aliasOptions[0].value);
      }
    }

    // Initialize yarn certification details with aggregated yarn codes
    this.initializeAggregatedYarnCertificationDetails();

    this.workOrderFabricForm.controls.yarnCertificationDetails.getRawValue().forEach((yarnControl) => {
      this.onCertificationChange(
        { value: yarnControl.certificationReferences } as MultiSelectChangeEvent,
        yarnControl.yarnCode,
        this.workOrderFabricForm.controls.fabricCode.value,
      );
    });

    //Disable form by Rol
    if (!this.canEdit) {
      this.workOrderFabricForm.disable();
    }

    this.workOrderFabricForm.markAllAsTouched();
  }

  /**
   * Initializes yarn certification details form array with only unique yarn codes
   * Sums up use percentages for duplicate yarn codes
   */
  private initializeAggregatedYarnCertificationDetails(): void {
    const yarnDetails = this.workOrderFabricForm.getRawValue().fabric?.greige?.yarnDetails || [];

    if (!yarnDetails || yarnDetails.length === 0) {
      return;
    }

    // Get existing yarn certification details to preserve certifications
    const existingCertDetails = this.workOrderFabricForm.getRawValue().yarnCertificationDetails || [];
    // On a locked Work Order the fabric form arrives disabled; the new controls must not re-enable it
    const wasDisabled = this.workOrderFabricForm.disabled;

    // Group by yarn code and sum percentages
    const aggregatedYarns = yarnDetails.reduce(
      (acc, yarnDetail) => {
        const yarnCode = yarnDetail.yarnCode;

        if (!acc[yarnCode]) {
          acc[yarnCode] = {
            yarnCode: yarnCode,
            usePercentage: 0,
          };
        }

        acc[yarnCode].usePercentage += yarnDetail.usePercentage || 0;

        return acc;
      },
      {} as Record<string, { yarnCode: string; usePercentage: number }>,
    );

    // Clear existing controls
    this.workOrderFabricForm.controls.yarnCertificationDetails.clear();

    // Create form controls only for unique yarn codes
    Object.values(aggregatedYarns).forEach((aggregatedYarn) => {
      // Find existing certifications for this yarn code
      const existingCert = existingCertDetails.find((cert) => cert.yarnCode === aggregatedYarn.yarnCode);

      const yarnCertControl = this.fb.group({
        yarnCode: new FormControl(aggregatedYarn.yarnCode, { nonNullable: true }),
        certificationReferences: new FormControl<string[]>(existingCert?.certificationReferences || [], { nonNullable: true }),
        usePercentage: new FormControl(parseFloat(aggregatedYarn.usePercentage.toFixed(2))),
      });

      this.workOrderFabricForm.controls.yarnCertificationDetails.push(yarnCertControl as FormGroup);
    });

    if (wasDisabled) {
      this.workOrderFabricForm.disable({ emitEvent: false });
    }

    this.updateYarnCertificationsMap();
  }

  getTotalQuantity() {
    //Add all the quantities from the garmentDetailsForms
    let totalQuantity = 0;
    this.workOrderFabricForm.controls.garmetDetails.controls.forEach((form) => {
      const quantity = form.get('quantity')?.value;
      if (quantity) {
        totalQuantity += quantity;
      }
    });
    // Round to 2 decimals to avoid binary floating-point drift (e.g. 293.07 + 329.23 + 322.82 + 170.80 = 1115.9199999999998)
    return Math.round(totalQuantity * 100) / 100;
  }

  getYarnCertifications(yarn: Yarn | null): Certification[] {
    if (!yarn) {
      return [];
    }
    return this.certifications.filter((cert) => {
      return cert.allCompositions || yarn.yarnComposition.some((comp) => cert.includedCompositions.includes(comp.compositionCode));
    });
  }

  getTotalCost() {
    if (this.isGreigeOrder) {
      return this.workOrderFabricForm.controls.quantityInUOM.value * this.workOrderFabricForm.controls.unitPrice.value;
    }

    let total = 0;
    this.workOrderFabricForm.controls.garmetDetails.controls.forEach((form) => {
      const quantity = form.get('quantity')?.value ?? 0;
      const unitPrice = form.get('unitPrice')?.value ?? 0;
      total += quantity * unitPrice;
    });
    return total;
  }

  public async colorsTypeAhead(event: AutoCompleteCompleteEvent): Promise<void> {
    const term = event.query.toLowerCase().trim();
    const isSample = this.orderType === WorkOrderType.Sample;

    if (!isSample && _.isNil(this.customer?.id)) {
      const message = this.translateService.instant('WorkOrder_Details_No_Client_Select_Colors_Message');
      const title = this.translateService.instant('WorkOrder_Details_No_Client_Selected');
      GlobalMessageUtil.info(title ? title : 'No Client Selected', message ? message : 'Please select a client to search for colors.');
      this.filteredColors = [];
      return;
    }

    const dataSearch: searchColorByFabric = {
      customerReference: isSample ? undefined : this.customer?.id,
      fabricCode: this.workOrderFabricForm.controls.fabricCode.value,
      colorName: term,
    };

    const { data } = await lastValueFrom(this.labDipRequestProxyService.getColorByFabric(dataSearch).pipe(debounceTime(500)));
    const addedColorReferences = new Set(this.workOrderFabricForm.controls.garmetDetails.controls.map((c) => c.value.colorReference));
    this.filteredColors = (data ?? []).filter((color) => !addedColorReferences.has(color.id));

    if (_.isEmpty(data)) {
      const title = this.translateService.instant('Work_Order_Details_No_Colors_Found');
      const message = this.translateService.instant('Work_Order_Details_Create_Lab_Dip_Request_Message');
      GlobalMessageUtil.warn(
        title ? title : 'No Colors Found',
        message
          ? message
          : 'There are no colors available for the selected client. Please add colors in Lab Dip Request for client and fabric.',
        10000,
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientProductOrders']) {
      this.clientProductOrderOptions = this.clientProductOrders.map((order) => ({
        label: order.productOrderNumber,
        value: order.productOrderNumber,
      }));
    }

    if (changes['customer'] || changes['workOrderFabricForm']) {
      this.updateAliasOptions();
    }

    if (changes['certifications'] || changes['workOrderFabricForm']) {
      this.updateYarnCertificationsMap();
    }
  }

  updateYarnCertificationsMap(): void {
    if (!this.workOrderFabricForm?.controls?.yarnCertificationDetails?.controls) return;

    this.workOrderFabricForm.controls.yarnCertificationDetails.controls.forEach((yarnControl) => {
      const yarnCode = yarnControl.value?.yarnCode;
      if (yarnCode) {
        const yarn = this.getYarnByCode(yarnCode);
        this.yarnCertificationsMap[yarnCode] = this.getYarnCertifications(yarn);
      }
    });
  }

  updateAliasOptions(): void {
    const aliases =
      this.workOrderFabricForm.getRawValue().fabric?.aliases?.filter((alias) => alias.customerReference === this.customer?.id) || [];

    this.aliasOptions = aliases.map((alias) => ({
      label: alias.alias || 'Sin nombre',
      value: alias.alias || '',
    }));
  }

  manageAliases() {
    if (_.isNil(this.customer)) {
      const title = this.translateService.instant('Work_Order_Details_No_Customer_Selected');
      const message = this.translateService.instant('Work_Order_Details_Select_Customer_Alias_Message');
      GlobalMessageUtil.info(title ?? 'No customer selected', message ?? 'Please select a customer to manage aliases.');
      return;
    }
    this.modalService.open(
      FabricAliasManageModalComponent,
      { modalClass: 'modal_small' },
      { fabric: this.workOrderFabricForm.getRawValue().fabric, customerReference: this.customer.id },
    );
  }

  // Modifica el método existente para ser más simple
  getCustomerIdentifier(): string {
    return this.workOrderFabricForm.get('selectedAlias')?.value || '';
  }

  onSelectColor(event: AutoCompleteSelectEvent) {
    this.colorSearch.setValue('');
    this.addColorAtList(event.value);
    if (!this.customerColors.find((color) => color.id === event.value.id)) {
      this.customerColors.push(event.value);
    }
  }

  buildUniqueKey(yarnCode: string, fabricCode: string): string {
    return `${fabricCode}-${yarnCode}`;
  }

  onCertificationChange(event: MultiSelectChangeEvent, yarnCode: string, fabricCode: string): void {
    const certifications: string[] = event.value || [];

    // Update the form control for this yarn code
    const yarnCertControl = this.workOrderFabricForm.controls.yarnCertificationDetails.controls.find((c) => c.value.yarnCode === yarnCode);

    if (yarnCertControl) {
      yarnCertControl.controls.certificationReferences.setValue(certifications, { emitEvent: false });
    }

    const command: MatchingItemMasterCommand = {
      yarnCode: yarnCode,
      certificationReference: certifications,
    };
    this.yarnProxyService.getItemMastersByYarnCode([command]).subscribe({
      next: (response) => {
        if (!_.isNil(response.data) && !_.isNil(response.data[yarnCode])) {
          const yarnUniqueKey = this.buildUniqueKey(yarnCode, fabricCode);
          const itemMaster = response.data[yarnCode]!;
          itemMaster.fieldToDisplay = `(${itemMaster.itemCode}) - ${this.localeHelperService.getLocalizedValue(itemMaster.itemDescription)}`;
          this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = response.data![yarnCode];
        } else {
          const yarnUniqueKey = this.buildUniqueKey(yarnCode, fabricCode);
          this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = null; // Ensure it exists
        }
      },
      error: () => {
        const yarnUniqueKey = this.buildUniqueKey(yarnCode, fabricCode);
        this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = null; // Ensure it exists even if error occurs
        console.error(`Error fetching item master for yarn code: ${yarnCode}`);
      },
    });
  }

  private addColorAtList(color: DyeingColor | string): void {
    if (typeof color === 'string') return;
    const matchFabric = this.workOrderFabricForm.controls.garmetDetails.controls.find((c) => c.value.colorReference === color.id);

    if (matchFabric) return;
    this.workOrderFabricForm.controls.garmetDetails.push(this.createColorForm(color));
  }

  private createColorForm(color: DyeingColor): FormGroup {
    return this.fb.group({
      productOrderNumber: new FormControl(null, { nonNullable: true, validators: [Validators.required] }),
      colorReference: new FormControl(color.id),
      quantity: new FormControl(0, [Validators.required, Validators.min(0.01)]),
      unitPrice: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(0.01)] }),
    });
  }

  getgarmentDetailsForm(colorReference: string): FormGroup {
    const colorForm = this.workOrderFabricForm.controls.garmetDetails.controls.find((form) => {
      const color = form.get('colorReference')?.value;
      return color === colorReference;
    }) as FormGroup;
    return colorForm;
  }

  getColorByReference(colorReference: string) {
    return this.customerColors.find((color) => color.id === colorReference);
  }

  deleteCard() {
    this.removeFabric.emit(this.workOrderFabricForm.controls.fabricCode.value);
  }

  enterPackingTypeEditMode(): void {
    this.originalPackingType = this.workOrderFabricForm.controls.packingType.value;
    this.workOrderFabricForm.controls.packingType.enable();
    this.isPackingTypeEditMode = true;
  }

  cancelPackingTypeEdit(): void {
    this.workOrderFabricForm.controls.packingType.setValue(this.originalPackingType);
    this.workOrderFabricForm.controls.packingType.disable();
    this.isPackingTypeEditMode = false;
  }

  commitPackingTypeEdit(): void {
    const newValue = this.workOrderFabricForm.controls.packingType.value;
    if (newValue !== this.originalPackingType && newValue !== null) {
      this.savePackingType.emit({
        fabricCode: this.workOrderFabricForm.controls.fabricCode.value,
        packingType: newValue,
      });
    }
    this.workOrderFabricForm.controls.packingType.disable();
    this.isPackingTypeEditMode = false;
  }

  removeGarmentDetail(garmentDetail: FormGroup<GarmentDetailForm>): void {
    const colorReference = garmentDetail.value.colorReference;

    const garmetDetails = this.workOrderFabricForm.controls.garmetDetails as FormArray<FormGroup<GarmentDetailForm>>;

    const index = garmetDetails.controls.findIndex((form) => form.controls.colorReference.value === colorReference);

    if (index > -1) {
      garmetDetails.removeAt(index);
    } else {
      console.warn(`No GarmentDetail found for colorReference=${colorReference}`);
    }
  }
}
